import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  CloudOff as InactiveIcon,
  Refresh as RefreshIcon,
  Cable as TestIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { twonApi } from '../../../services/twonService';
import TwoNConfigDialog from './TwoNConfigDialog';

export default function CommanderList() {
  const { enqueueSnackbar } = useSnackbar();
  const [commanders, setCommanders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [selectedCommander, setSelectedCommander] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commanderToDelete, setCommanderToDelete] = useState(null);
  const [syncingCommanders, setSyncingCommanders] = useState(new Set());
  const [testingCommanders, setTestingCommanders] = useState(new Set());
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCommanders();
  }, []);

  const loadCommanders = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await twonApi.getAllConfigs();
      const commandersData = response.data.data || [];
      console.log('Loaded commanders:', commandersData);
      // Debug: Log first commander's status fields
      if (commandersData.length > 0) {
        console.log('First commander status:', {
          last_sync_at: commandersData[0].last_sync_at,
          last_sync_success: commandersData[0].last_sync_success,
          connection_status: commandersData[0].connection_status
        });
      }
      setCommanders(commandersData);
    } catch (error) {
      console.error('Failed to load commanders:', error);
      setError('Failed to load Commander configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCommander = () => {
    setSelectedCommander(null);
    setConfigDialogOpen(true);
  };

  const handleEditCommander = (commander) => {
    setSelectedCommander(commander);
    setConfigDialogOpen(true);
  };

  const handleConfigSaved = () => {
    setConfigDialogOpen(false);
    loadCommanders();
  };

  const handleDeleteClick = (commander) => {
    setCommanderToDelete(commander);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await twonApi.deleteConfig(commanderToDelete.id);
      setDeleteDialogOpen(false);
      setCommanderToDelete(null);
      enqueueSnackbar('Commander deleted successfully', { variant: 'success' });
      loadCommanders();
    } catch (error) {
      console.error('Failed to delete commander:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to delete Commander configuration',
        { variant: 'error' }
      );
    }
  };

  const handleSyncCommander = async (commanderId) => {
    try {
      setSyncingCommanders(prev => new Set([...prev, commanderId]));
      await twonApi.syncCommander(commanderId);
      enqueueSnackbar('Sync started successfully', { variant: 'success' });
      loadCommanders();
    } catch (error) {
      console.error('Failed to start sync:', error);
      enqueueSnackbar(
        error.response?.data?.error || error.message || 'Failed to start sync',
        { variant: 'error' }
      );
    } finally {
      setSyncingCommanders(prev => {
        const newSet = new Set(prev);
        newSet.delete(commanderId);
        return newSet;
      });
    }
  };

  const handleSyncAll = async () => {
    try {
      const response = await twonApi.syncAllCommanders();
      if (response.data.success) {
        enqueueSnackbar(
          `Multi-Commander sync started. ${response.data.data.total} Commanders queued.`,
          { variant: 'success' }
        );
        loadCommanders();
      }
    } catch (error) {
      console.error('Failed to start multi-Commander sync:', error);
      enqueueSnackbar(
        error.response?.data?.error || error.message || 'Failed to start multi-Commander sync',
        { variant: 'error' }
      );
    }
  };

  const handleTestConnection = async (commander) => {
    try {
      setTestingCommanders(prev => new Set([...prev, commander.id]));

      // Use the stored Commander test endpoint (same as dialog)
      const response = await twonApi.testCommanderConnection(commander.id);

      if (response.data.success) {
        enqueueSnackbar(
          `Connection test successful! ${commander.commander_name} is reachable.`,
          { variant: 'success' }
        );
        // Reload commanders to get updated status
        await loadCommanders();
      } else {
        throw new Error(response.data.error || 'Connection test failed');
      }
    } catch (error) {
      console.error('Failed to test connection:', error);
      enqueueSnackbar(
        error.response?.data?.error || error.message || 'Connection test failed',
        { variant: 'error' }
      );
    } finally {
      setTestingCommanders(prev => {
        const newSet = new Set(prev);
        newSet.delete(commander.id);
        return newSet;
      });
    }
  };

  const getStatusChip = (commander) => {
    if (!commander.is_active) {
      return <Chip icon={<InactiveIcon />} label="Inactive" size="small" color="default" />;
    }

    // Check if we have test/sync history to determine status
    // last_sync_success can be true, false, or null/undefined
    if (commander.last_sync_at && commander.last_sync_success !== null && commander.last_sync_success !== undefined) {
      if (commander.last_sync_success) {
        return <Chip icon={<SuccessIcon />} label="Connected" size="small" color="success" />;
      } else {
        return <Chip icon={<ErrorIcon />} label="Error" size="small" color="error" />;
      }
    }

    // No test/sync history yet - show as configured but not tested
    return <Chip icon={<InactiveIcon />} label="Not Tested" size="small" color="default" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">2N Access Commanders</Typography>
        <Box>
          <Tooltip title="Refresh list">
            <IconButton onClick={loadCommanders} sx={{ mr: 1 }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<SyncIcon />}
            onClick={handleSyncAll}
            sx={{ mr: 1 }}
            disabled={commanders.filter(c => c.is_active && c.sync_enabled).length === 0}
          >
            Sync All
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddCommander}
          >
            Add Commander
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {commanders.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" align="center">
              No Commander configurations found. Click "Add Commander" to configure your first 2N Access Commander.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>URL</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Sync</TableCell>
                <TableCell align="center">Last Sync</TableCell>
                <TableCell align="center">Devices</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {commanders.map((commander) => (
                <TableRow key={commander.id}>
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {commander.commander_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {commander.commander_url}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {getStatusChip(commander)}
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={commander.sync_enabled}
                      disabled
                      size="small"
                    />
                    {commander.sync_enabled && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Every {commander.sync_interval_minutes || 15} min
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(commander.last_sync_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body1">
                      {commander.device_count || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Test Connection">
                      <IconButton
                        size="small"
                        onClick={() => handleTestConnection(commander)}
                        disabled={!commander.is_active || testingCommanders.has(commander.id)}
                      >
                        {testingCommanders.has(commander.id) ? (
                          <CircularProgress size={20} />
                        ) : (
                          <TestIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Sync Now">
                      <IconButton
                        size="small"
                        onClick={() => handleSyncCommander(commander.id)}
                        disabled={!commander.is_active || syncingCommanders.has(commander.id)}
                      >
                        {syncingCommanders.has(commander.id) ? (
                          <CircularProgress size={20} />
                        ) : (
                          <SyncIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEditCommander(commander)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(commander)}
                        disabled={commander.device_count > 0}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Config Dialog */}
      <TwoNConfigDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        onSaved={handleConfigSaved}
        commanderConfig={selectedCommander}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Commander Configuration?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{commanderToDelete?.commander_name}"?
          </Typography>
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>
            This action cannot be undone. All linked devices will need to be reassigned.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}